import { PdfLayout } from '../PdfLayout';
import { FileJson, FileCode2, Database, Lock, Webhook } from 'lucide-react';

const benefits = [
  {
    icon: FileJson,
    title: 'Live Values as JSON or XML',
    text: 'Switch on the Data API and tools such as Grafana or Excel read every live tag value from /api/data/tags.json or tags.xml.',
  },
  {
    icon: Database,
    title: 'Historian API',
    text: 'Read up to 10 tags per request over any time range, raw or aggregated (avg, min, max, sum and more), with long ranges served from hourly and daily rollups — for BI tools, MES or a spreadsheet.',
  },
  {
    icon: FileCode2,
    title: 'Full REST API',
    text: 'Everything the web app does goes through /api: write tags, acknowledge alarms, read data tables, run named queries and workflows.',
  },
  {
    icon: Lock,
    title: 'Versioned Config API & Tokens',
    text: 'Provision and audit appliances from CI over /api/v1, with scoped, revocable API tokens and an OpenAPI 3 document.',
  },
  {
    icon: Webhook,
    title: 'Push to ERP, MES & More',
    text: 'Outbound REST calls on a timer or tag change, alarm webhooks, remote SQL databases, MQTT Sparkplug B and a built-in OPC-UA server.',
  },
];

export function ApiAccess() {
  return (
    <PdfLayout
      category="Integration"
      headline="Connect Anything with the"
      headlineHighlight="REST API"
      description="Live tag values and historian records are one HTTP request away. Read live values as JSON or XML, query trends, automate anything the app can do, and push production data to your ERP, MES or cloud — every call checked against the caller's role or token scope."
      accentColor="#3b82f6"
      benefits={benefits}
    >
      <ApiMockup />
    </PdfLayout>
  );
}

const K = 'text-[#7c3aed]';
const P = 'text-[#64748b]';
const S = 'text-[#16a34a]';

function ApiMockup() {
  return (
    <div className="glass-panel p-3 w-full max-w-[280px]">
      <div className="flex items-center gap-2 mb-2 px-1">
        <div className="flex gap-1.5">
          <div className="w-2 h-2 rounded-full bg-status-fault/60" />
          <div className="w-2 h-2 rounded-full bg-status-idle/60" />
          <div className="w-2 h-2 rounded-full bg-status-running/60" />
        </div>
        <span className="text-[9px] text-text-muted">API Explorer</span>
      </div>

      {/* Live values (Public Data API) */}
      <div className="glass-card p-2 mb-1.5">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-[6px] font-bold px-1 py-0.5 rounded bg-status-running/20 text-status-running">GET</span>
          <span className="text-[7px] font-mono text-text-secondary">/api/data/tags.json</span>
        </div>
        <div className="bg-[#0f172a] rounded p-1.5 font-mono text-[6.5px] leading-relaxed whitespace-pre">
          <span className={P}>{'{ '}</span><span className={K}>"devices"</span><span className={P}>{': [{'}</span>{'\n'}
          <span className={K}>  "name"</span><span className={P}>:</span> <span className={S}>"CNC-01"</span><span className={P}>,</span>{'\n'}
          <span className={K}>  "tags"</span><span className={P}>{': [{'}</span>{'\n'}
          <span className={K}>    "path"</span><span className={P}>:</span> <span className={S}>"spindle_speed"</span><span className={P}>,</span>{'\n'}
          <span className={K}>    "value"</span><span className={P}>:</span> <span className={S}>"4218"</span><span className={P}>,</span>{'\n'}
          <span className={K}>    "quality"</span><span className={P}>:</span> <span className={S}>"good"</span><span className={P}>,</span>{'\n'}
          <span className={K}>    "timestamp"</span><span className={P}>:</span> <span className={S}>"2026-10-15T14:02:11Z"</span>{'\n'}
          <span className={P}>{'  }] }] }'}</span>
        </div>
      </div>

      {/* Historian API */}
      <div className="glass-card p-2 mb-1.5">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-[6px] font-bold px-1 py-0.5 rounded bg-status-running/20 text-status-running">GET</span>
          <span className="text-[7px] font-mono text-text-secondary">/api/data/history.json</span>
        </div>
        <div className="text-[6.5px] font-mono text-text-muted mb-1 whitespace-pre">
          {'?tags=CNC-01:spindle_speed\n&start=-8h&window=1h&aggregate=mean'}
        </div>
        <div className="bg-[#0f172a] rounded p-1.5 font-mono text-[6.5px] leading-relaxed whitespace-pre">
          <span className={P}>{'{ '}</span><span className={K}>"tags"</span><span className={P}>{': [{'}</span>{'\n'}
          <span className={K}>  "device"</span><span className={P}>:</span> <span className={S}>"CNC-01"</span><span className={P}>,</span>{'\n'}
          <span className={K}>  "tagPath"</span><span className={P}>:</span> <span className={S}>"spindle_speed"</span><span className={P}>,</span>{'\n'}
          <span className={K}>  "points"</span><span className={P}>{': ['}</span>{'\n'}
          <span className={P}>{'    { '}</span><span className={K}>"t"</span><span className={P}>:</span> <span className={S}>"2026-10-15T06:00:00Z"</span><span className={P}>,</span> <span className={K}>"v"</span><span className={P}>:</span> <span className={S}>"4196.2"</span><span className={P}>{' },'}</span>{'\n'}
          <span className={P}>{'    … ] }] }'}</span>
        </div>
      </div>

      {/* Format toggle */}
      <div className="glass-card p-2">
        <span className="text-[7px] text-text-muted block mb-1">Live Values Format</span>
        <div className="flex gap-1.5">
          <div className="flex-1 text-center py-1 rounded text-[7px] font-semibold text-white" style={{ backgroundColor: '#3b82f6' }}>
            JSON
          </div>
          <div className="flex-1 text-center py-1 rounded text-[7px] font-medium text-text-secondary border border-glass-border">
            XML
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-2 px-1">
        <span className="text-[8px] text-text-muted">Bearer token auth</span>
        <span className="text-[8px] text-status-running font-medium">200 OK</span>
      </div>
    </div>
  );
}
